import { PricingRule } from '@/types/revenue-manager';
import { delay, API_ENDPOINTS, apiClient, ApiResponse, USE_MOCK } from './client';
import { mockPricingRulesData } from '@/data/mock-data';
import { transformPricingRuleFromAPI, ApiPricingRule, transformPricingRuleForAPI, RawApiPricingRule } from '@/utils/pricing-rule-transform';

export interface PricingRulesResponse {
  success: boolean;
  data: PricingRule[];
  total: number;
  propertyId: string;
}

// ============ API Functions ============

export async function fetchPricingRules(propertyId: string): Promise<PricingRulesResponse> {
  if (USE_MOCK) {
    await delay();
    const rules = mockPricingRulesData[propertyId] || [];
    return {
      success: true,
      data: rules,
      total: rules.length,
      propertyId,
    };
  }
  
  // Use generic API endpoint with filters parameter
  // Fetch all columns needed for transformation
  const filters = JSON.stringify([{
    resource_name: "PricingRule",
    columns: ["*", "property_id", "is_active", "rule_type"],
    column_filters: [{
      property_id: propertyId,
      // is_active: true,
      rule_type: "restriction"
    }]
  }]);
  
  const response = await apiClient.get<any>(
    `${API_ENDPOINTS.generic}?filters=${encodeURIComponent(filters)}`
  );
  
  // Extract data from the nested response structure
  const apiRulesData = response.data?.data?.[0]?.PricingRule_data || [];
  // console.log('Full API Response:', JSON.stringify(response.data, null, 2));
  // console.log('API rules data:', apiRulesData);
  // console.log('API rules data length:', apiRulesData.length);
  
  // if (apiRulesData.length > 0) {
  //   console.log('First rule structure:', JSON.stringify(apiRulesData[0], null, 2));
  // }
  
  // Transform API format to internal format
  const transformedRules = apiRulesData.map((apiRule: unknown, index: number) => {
    // console.log(`Transforming rule ${index}:`, apiRule);
    try {
      const transformed = transformPricingRuleFromAPI(apiRule as RawApiPricingRule);
      // console.log(`Transformed rule ${index}:`, transformed);
      return transformed;
    } catch (error) {
      console.error(`Error transforming rule ${index}:`, error);
      throw error;
    }
  });
  // console.log('Transformed rules:', transformedRules);
  return {
    success: response.data.success,
    data: transformedRules,
    total: response.data?.data?.[0]?.total || 0,
    propertyId,
  };
}

export async function createPricingRule(
  propertyId: string,
  rule: any
): Promise<ApiResponse<PricingRule>> {
  if (USE_MOCK) {
    await delay();
    const now = new Date().toISOString();
    const newRule: PricingRule = {
      ...rule,
      id: `rule-${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };
    
    if (!mockPricingRulesData[propertyId]) {
      mockPricingRulesData[propertyId] = [];
    }
    mockPricingRulesData[propertyId].push(newRule);
    
    return {
      success: true,
      data: newRule,
    };
  }
  
  // Use unified revenue rules endpoint
  const response = await apiClient.post<ApiResponse<PricingRule>>(
    API_ENDPOINTS.revenueRules.create,
    rule
  );
  return response.data;
}

export async function updatePricingRule(
  propertyId: string,
  ruleId: string,
  updates: any
): Promise<ApiResponse<PricingRule | null>> {
  if (USE_MOCK) {
    await delay();
    const rules = mockPricingRulesData[propertyId] || [];
    const index = rules.findIndex(r => r.id === ruleId);
    
    if (index === -1) {
      return { success: false, data: null };
    }
    
    rules[index] = { ...rules[index], ...updates, updatedAt: new Date().toISOString() };
    return {
      success: true,
      data: rules[index],
    };
  }
  
  // Use unified revenue rules endpoint with POST method
  // Add id to the payload (same as create but with id)
  const payload = {
    ...updates,
    id: ruleId
  };
  
  const response = await apiClient.post<ApiResponse<PricingRule | null>>(
    API_ENDPOINTS.revenueRules.create,
    payload
  );
  return response.data;
}

export async function deletePricingRule(
  propertyId: string,
  ruleId: string
): Promise<ApiResponse<null>> {
  if (USE_MOCK) {
    await delay();
    const rules = mockPricingRulesData[propertyId] || [];
    const index = rules.findIndex(r => r.id === ruleId);
    
    if (index !== -1) {
      rules.splice(index, 1);
    }
    return { success: true, data: null };
  }
  
  // Use generic API endpoint with DELETE method
  const payload = [{
    resource_name: "PricingRule",
    column_value_pair: {
      ids: [ruleId]
    }
  }];
  
  const response = await apiClient.delete<ApiResponse<null>>(
    API_ENDPOINTS.generic,
    { data: payload }
  );
  return response.data;
}

export async function togglePricingRule(
  propertyId: string,
  ruleId: string,
  isActive: boolean
): Promise<ApiResponse<PricingRule | null>> {
  if (USE_MOCK) {
    await delay(200);
    const rules = mockPricingRulesData[propertyId] || [];
    const rule = rules.find(r => r.id === ruleId);
    
    if (!rule) {
      return { success: false, data: null };
    }
    
    rule.isActive = !rule.isActive;
    rule.updatedAt = new Date().toISOString();
    
    return {
      success: true,
      data: rule,
    };
  }
  
  const payload = [
    {
      resource_name: "PricingRule",
      column_value_pair: [
        {
          id: parseInt(ruleId),
          is_active: !isActive
        }
      ]
    }
  ];
  
  const response = await apiClient.patch<ApiResponse<PricingRule | null>>(
    API_ENDPOINTS.generic,
    payload
  );
  return response.data;
}

export async function runPricingRule(
  propertyId: string,
  ruleId: string
): Promise<ApiResponse<{ executed: boolean; affectedRooms: number }>> {
  if (USE_MOCK) {
    await delay(500);
    const rules = mockPricingRulesData[propertyId] || [];
    const rule = rules.find(r => r.id === ruleId);
    
    if (rule) {
      rule.lastRun = new Date().toISOString();
    }
    
    return {
      success: true,
      data: { executed: true, affectedRooms: Math.floor(Math.random() * 50) + 10 },
    };
  }
  
  const response = await apiClient.post<ApiResponse<{ executed: boolean; affectedRooms: number }>>(
    API_ENDPOINTS.pricingRules.run(propertyId, ruleId),
    {}
  );
  return response.data;
}

export async function importPricingRules(
  targetPropertyIds: string[],
  rules: Omit<PricingRule, 'id' | 'createdAt' | 'updatedAt'>[]
): Promise<ApiResponse<{ importedCount: number }>> {
  if (USE_MOCK) {
    await delay(500);
    const now = new Date().toISOString();
    let importedCount = 0;
    
    for (const propertyId of targetPropertyIds) {
      if (!mockPricingRulesData[propertyId]) {
        mockPricingRulesData[propertyId] = [];
      }
      
      for (const rule of rules) {
        const newRule: PricingRule = {
          ...rule,
          id: `rule-${Date.now()}-${importedCount}`,
          createdAt: now,
          updatedAt: now,
        };
        mockPricingRulesData[propertyId].push(newRule);
        importedCount++;
      }
    }
    
    return {
      success: true,
      data: { importedCount },
    };
  }
  
  const response = await apiClient.post<ApiResponse<{ importedCount: number }>>(
    API_ENDPOINTS.revenueRules.import,
    { targetPropertyIds, rules }
  );
  return response.data;
}

export async function exportPricingRules(
  propertyId: string
): Promise<ApiResponse<PricingRule[]>> {
  if (USE_MOCK) {
    await delay();
    return {
      success: true,
      data: mockPricingRulesData[propertyId] || [],
    };
  }
  
  const response = await apiClient.get<ApiResponse<PricingRule[]>>(
    API_ENDPOINTS.pricingRules.export(propertyId)
  );
  return response.data;
}
